import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Modal from "../../components/Modal";
import { useUploadExcelSheet } from "./uploadExcelSheetData";
import uploadExcelImg from "../../assets/images/uploadExcel.png";
import excelTemplate from "../../assets/documents/Excel_upload_template_filled.xlsx";
import "./index.scss";

// ── Download the bundled xlsx template ───────────────────────────────────────
const downloadTemplate = () => {
  const a = document.createElement("a");
  a.href = excelTemplate;
  a.download = "Excel_upload_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// ── Validation results table ──────────────────────────────────────────────────
const ValidationTable = ({ results, onClear }) => {
  const validCount = results.data.filter((r) => r.isValid).length;
  const invalidCount = results.data.filter((r) => !r.isValid).length;

  return (
    <div className="ues-validation-card">
      {/* Header */}
      <div className="ues-validation-header">
        <div className="ues-validation-header__left">
          <h2 className="ues-validation-header__title">Validation Results</h2>
          {results.message && (
            <p className="ues-validation-header__msg">{results.message}</p>
          )}
          <div className="ues-validation-header__badges">
            <span className="ues-badge ues-badge--total">
              {results.data.length} rows
            </span>
            {validCount > 0 && (
              <span className="ues-badge ues-badge--valid">
                ✓ {validCount} valid
              </span>
            )}
            {invalidCount > 0 && (
              <span className="ues-badge ues-badge--invalid">
                ✕ {invalidCount} errors
              </span>
            )}
          </div>
        </div>
        <button className="ues-btn ues-btn--clear" onClick={onClear}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="ues-validation-scroll">
        <table className="ues-validation-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Vendor ID</th>
              <th>Validation Message</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {results.data.map((row, i) => (
              <tr
                key={i}
                className={row.isValid ? "ues-vr--valid" : "ues-vr--invalid"}
              >
                <td className="ues-td--num">{i + 1}</td>
                <td>
                  <span className="ues-vendor-id">{row.vendorId || "—"}</span>
                </td>
                <td className="ues-validation-msg">
                  {row.validationMessage || "—"}
                </td>
                <td>
                  {row.isValid ? (
                    <span className="ues-status ues-status--valid">
                      ✓ Valid
                    </span>
                  ) : (
                    <span className="ues-status ues-status--invalid">
                      ✕ Error
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ onUpload }) => (
  <div className="ues-empty-state">
    <img
      src={uploadExcelImg}
      alt="Upload Excel"
      className="ues-empty-state__img"
    />
    <h2 className="ues-empty-state__title">Upload your Excel file</h2>
    <p className="ues-empty-state__desc">
      Upload an Excel sheet to validate and import entity onboarding data.
      <br />
      Download the template below to get started.
    </p>
    <div className="ues-empty-state__actions">
      <button className="ues-btn ues-btn--template" onClick={downloadTemplate}>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download Excel Template
      </button>
    </div>
  </div>
);

// ── View Documents modal (kept for folder upload view docs) ───────────────────
const ViewDocumentsModal = ({
  isOpen,
  onClose,
  row,
  docList,
  docListLoading,
  docBlobs,
}) => {
  if (!isOpen) return null;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Uploaded Documents"
      size="large"
      entityID={row?.entityId}
    >
      <div className="ues-docs-modal" style={{ padding: 16 }}>
        {docListLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <CircularProgress
              size={36}
              thickness={4}
              sx={{ color: "#4f46e5" }}
            />
          </Box>
        ) : docList.length === 0 ? (
          <p
            style={{ textAlign: "center", color: "#9ca3af", padding: "2rem 0" }}
          >
            No documents found.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 12,
            }}
          >
            {docList.map((doc) => {
              const blob = docBlobs[doc.id];
              return (
                <div
                  key={doc.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: 120,
                      background: "#f9fafb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {blob?.loading ? (
                      <CircularProgress size={24} sx={{ color: "#4f46e5" }} />
                    ) : blob?.url &&
                      /\.(png|jpg|jpeg|gif|webp)$/i.test(doc.fileType || "") ? (
                      <img
                        src={blob.url}
                        alt={doc.fileName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                        {doc.fileType || "FILE"}
                      </span>
                    )}
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <p
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.fileName}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: 12,
            borderTop: "1px solid #e5e7eb",
            marginTop: 12,
          }}
        >
          <button className="ues-btn ues-btn--cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const UploadExcelSheet = () => {
  const {
    uploadModalOpen,
    uploadType,
    handleOpenModal,
    handleCloseModal,
    file,
    handleFileChange,
    uploading,
    uploadProgress,
    handleUpload,
    validationResults,
    clearValidationResults,
    docModalOpen,
    docModalRow,
    docList,
    docListLoading,
    docBlobs,
    handleCloseDocModal,
  } = useUploadExcelSheet();

  const hasResults = validationResults && validationResults.data?.length > 0;

  return (
    <div className="ues-page">
      {/* Header */}
      <div className="ues-header">
        <div>
          <h1 className="ues-header__title">Upload Excel Sheet</h1>
          <p className="ues-header__sub">
            Upload an Excel file to validate and import entity onboarding data.
          </p>
        </div>
        <div className="uploader-buttons">
          <button
            className="ues-btn ues-btn--folder"
            onClick={() => handleOpenModal("folder")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Upload Folder
          </button>
          <button
            className="ues-btn ues-btn--upload"
            onClick={() => handleOpenModal("excel")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            Upload Excel Sheet
          </button>
        </div>
      </div>

      {/* Body: empty state OR validation results */}
      {hasResults ? (
        <ValidationTable
          results={validationResults}
          onClear={clearValidationResults}
        />
      ) : (
        <EmptyState onUpload={() => handleOpenModal("excel")} />
      )}

      {/* View Documents Modal */}
      <ViewDocumentsModal
        isOpen={docModalOpen}
        onClose={handleCloseDocModal}
        row={docModalRow}
        docList={docList}
        docListLoading={docListLoading}
        docBlobs={docBlobs}
      />

      {/* Upload Modal (Excel or Folder) */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={handleCloseModal}
        title={
          uploadType === "folder" ? "Upload Folder / ZIP" : "Upload Excel Sheet"
        }
        size="small"
      >
        <div className="ues-modal-body">
          <p className="ues-modal-body__hint">
            {uploadType === "folder"
              ? "Select a ZIP file containing onboarding documents."
              : "Select an Excel file (.xlsx, .xls) to bulk import onboarding data."}
          </p>

          {!uploading && (
            <label className="ues-upload-zone" htmlFor="ues-file-input">
              <input
                id="ues-file-input"
                type="file"
                accept={
                  uploadType === "folder" ? ".zip,.rar,.7z" : ".xlsx,.xls"
                }
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {file ? (
                <span className="ues-upload-zone__filename">
                  📎 {file.name}
                </span>
              ) : (
                <>
                  {uploadType === "folder" ? (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                  ) : (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="16 16 12 12 8 16" />
                      <line x1="12" y1="12" x2="12" y2="21" />
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                    </svg>
                  )}
                  <span>
                    {uploadType === "folder"
                      ? "Click to choose ZIP file"
                      : "Click to choose Excel file"}
                  </span>
                  <span className="ues-upload-zone__hint">
                    {uploadType === "folder"
                      ? ".zip, .rar, .7z"
                      : ".xlsx or .xls"}
                  </span>
                </>
              )}
            </label>
          )}

          {uploading && (
            <div className="ues-progress-wrap">
              <div className="ues-progress-circle-outer">
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={120}
                    thickness={4}
                    sx={{
                      color: "#e5e7eb",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={uploadProgress}
                    size={120}
                    thickness={4}
                    sx={{
                      color: uploadProgress === 100 ? "#16a34a" : "#4f46e5",
                      transition: "color 0.4s",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className="ues-progress-pct">{uploadProgress}%</span>
                    {uploadProgress === 100 && (
                      <span className="ues-progress-check">✓</span>
                    )}
                  </Box>
                </Box>
              </div>
              <p className="ues-progress-label">
                {uploadProgress === 100
                  ? "Upload complete!"
                  : uploadType === "folder"
                    ? "Uploading documents…"
                    : "Uploading Excel sheet…"}
              </p>
              <div className="ues-progress-bar-track">
                <div
                  className={`ues-progress-bar-fill${uploadProgress === 100 ? " ues-progress-bar-fill--done" : ""}`}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              {file && <p className="ues-progress-filename">📎 {file.name}</p>}
            </div>
          )}

          <div className="ues-modal-footer">
            <button
              className="ues-btn ues-btn--cancel"
              onClick={handleCloseModal}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="ues-btn ues-btn--submit"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? `${uploadProgress}%` : "Upload"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UploadExcelSheet;
